import { RecordAtLocation } from '@user/store/map/typings'
import { IUserPreferenceGeneralListItemType } from '@user/store/userPreferences/userPreferencesGeneral'
import { PREF } from '@user/components/HereMap/FilterChips/constants'
import {
  ActiveMapFilters,
  computeHiddenByFilters,
} from './recordsHiddenByFilters'

/**
 * WWM-17654 — After a record is created from the map, the user's active filters may hide it, so the
 * record they just saved is invisible and they can't tell what happened to it. Given the record that
 * was created and the active filters, work out the minimal preference change that reveals it.
 *
 * Delegates to SH-1143's `computeHiddenByFilters`, so the reveal rules stay in one place. The only
 * difference from `useRecordsHiddenByFilters.applyFilterUpdate` is that this never writes the
 * "My Records" preference — see `computeFilterUpdateForNewRecord`.
 */

/** Toast shown when saving a lead silently re-enabled the filters needed to see it. */
export const FILTERS_AUTO_UPDATED_LEADS =
  'Filters updated automatically to show leads'

/**
 * WWM-18096 — same idea for a knock status saved from the map. Singular "knock status" is the
 * ticket's wording, deliberately not matching FILTERS_AUTO_UPDATED_LEADS' plural.
 */
export const FILTERS_AUTO_UPDATED_KNOCK_STATUS =
  'Filters updated automatically to show knock status'

/**
 * The parts of a just-created record that decide whether the active filters would hide it.
 * Deliberately narrower than `RecordAtLocation` — a record we just created has no location ids worth
 * threading through, and the calculator doesn't read them.
 */
export type NewRecordForFilters = {
  /** Entity type of the created record, e.g. 'lead'. */
  entityType: string
  /**
   * Knock-status setting id the record inherited, or null. A lead added on top of an existing
   * knock-status pin inherits its status, and the client hides every record carrying a status when
   * no status is selected (useTileCache.filterLocationsNoKs) — so this is not optional detail.
   *
   * Accepts a string because the knock-status save input carries the id as one
   * (KnockStatusLocationBaseInput.knockStatusSettingsId).
   */
  knockStatusSettingsId: number | string | null
  /** Owner of the created record, for the "My Records" comparison. */
  ownerId: string | null
}

export type FilterUpdateForNewRecord = {
  /** Preference rows to write. Empty when the record is already visible. */
  batch: { type: IUserPreferenceGeneralListItemType; value: string }[]
  /** True when at least one preference actually needs to change. */
  changed: boolean
}

/**
 * Returns the preference batch that reveals `record` under the current `filters`, or an empty batch
 * when it is already visible.
 *
 * Callers must gate both the write and any user-facing confirmation on `changed` — saving a record
 * that the filters already show must not write preferences or announce a filter change.
 */
export function computeFilterUpdateForNewRecord(
  record: NewRecordForFilters,
  filters: ActiveMapFilters,
  currentUserId: string | null,
): FilterUpdateForNewRecord {
  const rawKsId = record.knockStatusSettingsId
  const parsedKsId = rawKsId == null || rawKsId === '' ? NaN : Number(rawKsId)

  const asRecordAtLocation: RecordAtLocation = {
    entityType: record.entityType,
    knockStatusSettingsId: Number.isNaN(parsedKsId) ? null : parsedKsId,
    ownerId: record.ownerId,
  }

  const result = computeHiddenByFilters(
    [asRecordAtLocation],
    filters,
    currentUserId,
  )

  const batch: {
    type: IUserPreferenceGeneralListItemType
    value: string
  }[] = []

  if (result.changed.entityTypes) {
    batch.push({ type: PREF.entityTypes, value: result.update.entityTypes })
  }
  if (result.changed.knockStatusIds) {
    batch.push({
      type: PREF.knockStatuses,
      value: result.update.knockStatusIds,
    })
  }
  // WWM-17654 decision 3: "My Records" keeps whatever value the user already had — if it's on it
  // stays on, if it's off it stays off. So there is deliberately NO `result.changed.myRecords`
  // branch here, unlike useRecordsHiddenByFilters.applyFilterUpdate. A record assigned to another
  // user while My Records is on therefore stays hidden, and that is accepted behaviour.

  return { batch, changed: batch.length > 0 }
}
